import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import SiteShell from "../components/SiteShell";
import HoverChat from "../components/HoverChat";
import Action from "../components/Action";
import { registerDeal, trackAction } from "../lib/api";
import { Field, inputStyle, primaryButtonStyle } from "./audit/StepEnergy";

/**
 * `/partners` — Register a Deal (EPC / NBFC / Auditor channel).
 * Ref: prompts/04_partner_portal.md. Businesslike partner-to-partner tone.
 */
const PARTNER_TYPES = ["EPC", "NBFC", "Auditor"];
const TIMELINES = [
  { v: "0-3m", label: "Next 3 months" },
  { v: "3-6m", label: "3 – 6 months" },
  { v: "6-12m", label: "6 – 12 months" },
  { v: "12m+", label: "12+ months" },
];
const STATES = ["TN", "KA", "MH", "GJ", "AP", "TS", "KL", "OTHER"];

export default function Partners() {
  const [open, setOpen] = useState(false);
  const [signinOpen, setSigninOpen] = useState(false);

  return (
    <SiteShell>
      <section
        style={{ maxWidth: 1040, margin: "0 auto",
          padding: "clamp(56px, 8vw, 100px) clamp(20px, 5vw, 60px) clamp(50px, 7vw, 90px)" }}
        data-testid="partners-page"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div className="gg-eyebrow" style={{ marginBottom: 10 }}>Channel partner portal</div>
            <h1 className="gg-hero-title" style={{ marginBottom: 18, fontSize: "clamp(32px, 5vw, 56px)", maxWidth: "22ch" }} data-testid="partners-headline">
              Register a deal, claim your rights, build with us.
            </h1>
          </div>
          <button
            type="button" onClick={() => setSigninOpen((v) => !v)} data-testid="partners-signin-toggle"
            style={{ background: "transparent", border: "1px solid var(--line)", borderRadius: 999,
              padding: "8px 14px", color: "var(--text-dim)", fontSize: 12.5, cursor: "pointer" }}
          >
            Returning partner? Sign in
          </button>
        </div>

        {signinOpen && (
          <div data-testid="partners-signin-stub" style={{
            marginTop: 14, padding: 16, border: "1px dashed var(--line-strong)", borderRadius: 12,
            color: "var(--text-dim)", fontSize: 13.5, maxWidth: 560,
          }}>
            <strong style={{ color: "var(--gold-soft)" }}>Coming in Phase 7.</strong> Partner sign-in
            lets you see every deal you've registered, its stage, and payouts. Meanwhile, all deals
            you register carry a millisecond-precision claim-priority timestamp — keep the ref id.
          </div>
        )}

        <p style={{ color: "var(--text-dim)", fontSize: 16.5, lineHeight: 1.7, maxWidth: 720, marginTop: 18 }}>
          Green Gold Engineering works alongside independent EPCs, NBFCs and energy auditors on{" "}
          <HoverChat term="gcpp">Group Captive</HoverChat> and Open-Access projects at ≥ 250 kWh.
          You bring the client and the field intelligence; we bring competitive cell pricing today,
          our own cells tomorrow, and full engineering support.
        </p>

        <div data-testid="partners-value-grid" style={{ display: "grid", gap: 14, marginTop: 32,
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {[
            ["Co-development", "On every SB / BO project you originate at ≥ 250 kWh."],
            ["Cell supply", "Imported LFP now · Made-in-India LFP + sodium-ion at commissioning."],
            ["Referral economics", "Meaningful, disclosed upon onboarding."],
            ["Engineering support", "Design reviews, sizing, BMS specs, digital-twin data."],
          ].map(([t, b]) => (
            <div key={t} className="gg-glass" style={{ padding: 16 }}>
              <div style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 17, color: "var(--paper)" }}>{t}</div>
              <div style={{ color: "var(--text-dim)", fontSize: 13.5, marginTop: 6, lineHeight: 1.55 }}>{b}</div>
            </div>
          ))}
        </div>

        <p style={{ color: "var(--text-cloud)", fontSize: 17, lineHeight: 1.7, marginTop: 40, maxWidth: 780 }}
           data-testid="partners-cta-line">
          When you have a lead, <Action route="register-deal" source="partners" onClick={() => { setOpen(true); trackAction({ route: "register-deal", source: "partners", sourceState: "cta" }); }}>register the deal here</Action>{" "}
          — your submission is timestamped to the millisecond and becomes your claim-priority anchor.
        </p>

        {open && <RegisterDrawer onClose={() => setOpen(false)} />}
      </section>
    </SiteShell>
  );
}

function RegisterDrawer({ onClose }) {
  const [f, setF] = useState({
    partner_name: "", partner_org: "", partner_role: "", partner_email: "", partner_phone: "",
    partner_type: "EPC", client_org: "", client_contact: "", client_sector: "",
    project_size_kwh: 250, location_state: "TN", location_pincode: "",
    timeline: "3-6m", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null);
  const set = (p) => setF({ ...f, ...p });

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.partner_email);
  const sizeOk = f.project_size_kwh >= 250;
  const canSubmit = f.partner_name.trim() && f.partner_org.trim() && f.partner_role.trim() &&
    emailOk && f.partner_phone.trim() && f.client_org.trim() && sizeOk && !busy;

  const submit = async (e) => {
    e.preventDefault(); if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      const data = await registerDeal({ ...f, project_size_kwh: Number(f.project_size_kwh) });
      setOk(data);
      trackAction({ route: "register-deal", source: "partners", sourceState: "submitted", audience: f.partner_type });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Submission failed.");
    } finally { setBusy(false); }
  };

  if (ok) return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="deal-confirmation"
      style={{ marginTop: 30, padding: 26, borderRadius: "var(--radius-lg)",
        background: "linear-gradient(180deg, rgba(31,170,89,0.14), rgba(31,170,89,0.02))",
        border: "1px solid rgba(31,170,89,0.4)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
        <Check size={18} color="var(--green-bright)" />
        <span className="gg-eyebrow" style={{ color: "var(--green-bright)", margin: 0 }}>Deal registered</span>
      </div>
      <h3 className="gg-h2">{ok.message}</h3>
      <p style={{ marginTop: 12, fontFamily: "Fraunces, Georgia, serif", fontSize: 20, color: "var(--gold-soft)" }}>
        Reference: <span data-testid="deal-ref-id">{ok.ref_id}</span>
      </p>
      <p style={{ marginTop: 6, color: "var(--text-mute)", fontSize: 13, fontFamily: "monospace" }} data-testid="deal-registered-at">
        Timestamp: {ok.registered_at}
      </p>
      <p style={{ marginTop: 16, color: "var(--text-cloud)", fontSize: 15.5, lineHeight: 1.7 }}>
        Next: <Action route="book-tour" queryParams={{ visit_type: "partner-onboarding" }} source="partners-confirmation">book an onboarding conversation</Action>.
      </p>
    </motion.div>
  );

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
      transition={{ duration: 0.35 }} data-testid="deal-drawer" style={{ marginTop: 30, overflow: "hidden" }}>
      <div style={{ padding: "24px 26px 28px", borderRadius: "var(--radius-lg)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
        border: "1px solid var(--line-strong)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div className="gg-eyebrow" style={{ marginBottom: 4 }}>Register a deal</div>
            <h3 className="gg-h2">Your lead, your claim.</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="deal-close" style={{
            background: "transparent", border: "1px solid var(--line)", borderRadius: 8,
            width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--text-dim)", cursor: "pointer" }}>
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit}>
          <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }}>
            <Field label="Your name*"><input required value={f.partner_name} onChange={(e) => set({ partner_name: e.target.value })} data-testid="deal-input-name" style={inputStyle} /></Field>
            <Field label="Organisation*"><input required value={f.partner_org} onChange={(e) => set({ partner_org: e.target.value })} data-testid="deal-input-org" style={inputStyle} /></Field>
            <Field label="Role / title*"><input required value={f.partner_role} onChange={(e) => set({ partner_role: e.target.value })} data-testid="deal-input-role" style={inputStyle} /></Field>
            <Field label="Email*"><input type="email" required value={f.partner_email} onChange={(e) => set({ partner_email: e.target.value })} data-testid="deal-input-email" style={{ ...inputStyle, borderColor: f.partner_email && !emailOk ? "rgba(255,120,120,0.5)" : "var(--line)" }} /></Field>
            <Field label="Phone*"><input type="tel" required value={f.partner_phone} onChange={(e) => set({ partner_phone: e.target.value })} data-testid="deal-input-phone" style={inputStyle} /></Field>
            <Field label="Partner type*">
              <div style={{ display: "flex", gap: 6 }}>{PARTNER_TYPES.map((t) => {
                const active = f.partner_type === t;
                return <button key={t} type="button" onClick={() => set({ partner_type: t })} data-testid={`deal-type-${t.toLowerCase()}`}
                  style={{ flex: 1, padding: "8px 10px", borderRadius: 10, background: active ? "rgba(244,169,31,0.14)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${active ? "rgba(244,169,31,0.5)" : "var(--line)"}`, color: active ? "var(--gold-soft)" : "var(--text-cloud)",
                    cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>{t}</button>;
              })}</div>
            </Field>
          </div>
          <div style={{ display: "grid", gap: 14, marginTop: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            <Field label="Client organisation*"><input required value={f.client_org} onChange={(e) => set({ client_org: e.target.value })} data-testid="deal-input-client-org" style={inputStyle} /></Field>
            <Field label="Client contact (optional)"><input value={f.client_contact} onChange={(e) => set({ client_contact: e.target.value })} data-testid="deal-input-client-contact" style={inputStyle} /></Field>
            <Field label="Client sector"><input value={f.client_sector} onChange={(e) => set({ client_sector: e.target.value })} placeholder="Textiles / hospital / …" data-testid="deal-input-sector" style={inputStyle} /></Field>
            <Field label="Project size (kWh)* — min 250" hint={!sizeOk ? <span style={{ color: "#ff8a8a" }}>Below floor — route via <Action route="reserve-block" source="partners">Reserve a Sovereign Block</Action> instead.</span> : "≥ 250 kWh SB / BO."}>
              <input type="number" min={0} required value={f.project_size_kwh} onChange={(e) => set({ project_size_kwh: parseFloat(e.target.value) || 0 })}
                data-testid="deal-input-size" style={{ ...inputStyle, borderColor: !sizeOk ? "rgba(255,120,120,0.5)" : "var(--line)" }} />
            </Field>
            <Field label="State*"><select value={f.location_state} onChange={(e) => set({ location_state: e.target.value })} data-testid="deal-input-state" style={inputStyle}>{STATES.map((s) => <option key={s} value={s} style={{ background: "var(--night-2)" }}>{s}</option>)}</select></Field>
            <Field label="Pincode (optional)"><input maxLength={6} value={f.location_pincode} onChange={(e) => set({ location_pincode: e.target.value.replace(/[^0-9]/g, "") })} data-testid="deal-input-pincode" style={inputStyle} /></Field>
            <Field label="Timeline*"><select value={f.timeline} onChange={(e) => set({ timeline: e.target.value })} data-testid="deal-input-timeline" style={inputStyle}>{TIMELINES.map((t) => <option key={t.v} value={t.v} style={{ background: "var(--night-2)" }}>{t.label}</option>)}</select></Field>
          </div>
          <div style={{ marginTop: 14 }}>
            <Field label="Notes"><textarea rows={3} value={f.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="deal-input-notes" style={{ ...inputStyle, resize: "vertical", minHeight: 72, fontFamily: "inherit" }} /></Field>
          </div>
          {err && <p data-testid="deal-submit-error" style={{ marginTop: 12, color: "#ff8a8a", fontSize: 13 }}>{err}</p>}
          <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
            <button type="submit" disabled={!canSubmit} data-testid="deal-submit" style={primaryButtonStyle(!canSubmit)}>
              {busy ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Loader2 size={14} className="animate-spin" /> Submitting…</span> : "Register the deal"}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
